import React, { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ControlledCombobox, {
  DropdownItem,
} from '@/components/form/controlled-combobox';
import ControlledSwitch from '@/components/form/controlled-switch';
import { COUNTRIES, STORE_COMPONENTS } from '@/lib/constants';
import ControlledDropdown from '@/components/form/controlled-dropdown';
import { IFormInputs } from '@/lib/schemas/create-code-form';
import { useUser } from './context';
import { useInformeraLocations } from '@/redux/apis/informera-api/hooks';
import { useGetV1LocationsQuery, User } from '@/redux/apis/frontend-api';

type SelectedComponentValue = {
  value?: string;
};

type NormalizedLocation = {
  location: string;
  country: string;
};

const findCountryCode = (countryName: string) => {
  return COUNTRIES.find(({ value }) => value === countryName)?.code;
};

const countries = COUNTRIES.map(({ text, value }) => {
  return { i18nKey: text, value };
});

const storeComponents = STORE_COMPONENTS.map(({ value }) => {
  return { i18nKey: `label.components.${value}`, value };
});

function appendUserLocation(
  user: User,
  locations: NormalizedLocation[]
): NormalizedLocation[] {
  const allSites = [...locations];
  const userLocationExists =
    user?.cdsLocation &&
    locations?.some(({ location }) => location === user.cdsLocation);

  if (!userLocationExists) {
    allSites.push({
      location: user.cdsLocation,
      country: findCountryCode(user.country.toLocaleLowerCase()) || '',
    });
  }

  return allSites;
}

const useCdsLocations = (
  params: { country?: string } = {},
  opts: { skip?: boolean } = { skip: false }
) => {
  const { data: cdsLocations = [] } = useGetV1LocationsQuery(params, opts);

  return useMemo(
    () =>
      cdsLocations.map((loc) => ({
        location: loc.name,
        country: loc.country,
      })),
    [cdsLocations]
  );
};

function Location() {
  // Hooks
  const { t } = useTranslation();
  const { control, watch, formState, setValue, resetField, getFieldState } =
    useFormContext<IFormInputs>();
  const { errors } = formState;
  const { user } = useUser();
  const targetGroup = watch('targetGroup');

  // Data
  const selectedComponents = watch('store.component');
  const onlyOnlineUsage = watch('onlyOnlineUsage');
  const country = watch('location.country');
  const selectedDepartments = watch('store.department');
  const location = watch('location.site');
  const defaultLocation = watch('defaultLocation');
  const countryCode = findCountryCode(country);

  // Data Hooks
  const { data: informeraLocations = [] } = useInformeraLocations(
    {
      country: countryCode,
    },
    {
      skip: targetGroup !== 'customer',
    }
  );
  const cdsLocations = useCdsLocations(
    {
      country: countryCode,
    },
    {
      skip: targetGroup !== 'coworker',
    }
  );

  // Normalize locations based on target group
  const allSites = useMemo(
    () => appendUserLocation(user, [...informeraLocations, ...cdsLocations]),
    [user, informeraLocations, cdsLocations]
  );
  const locatedInStore = informeraLocations.some(
    (informeraLocation) => informeraLocation.location === location
  );
  const sites = useMemo<DropdownItem[]>(
    () =>
      allSites
        .filter((c) => c.country === countryCode)
        .map(({ location }) => ({ i18nKey: location, value: location }))
        .sort((a, b) => a.value.localeCompare(b.value)),
    [countryCode, allSites]
  );

  const departments = useMemo<DropdownItem[]>(() => {
    if (!selectedComponents) {
      return [];
    }

    return STORE_COMPONENTS.filter((component) =>
      selectedComponents.some(
        (selectedComponent: SelectedComponentValue) =>
          selectedComponent.value === component.value
      )
    )
      .flatMap((component) => component.departments)
      .map(({ value }) => ({
        i18nKey: `label.components.departments.${value}`,
        value,
      }));
  }, [selectedComponents]);

  const { isDirty: isCountryDirty } = getFieldState(
    'location.country',
    formState
  );
  // Clear site when country changes
  useEffect(() => {
    if (isCountryDirty) {
      setValue('location.site', '', { shouldValidate: true });
    }
  }, [country, isCountryDirty, setValue]);

  // Clear selected departments if none are available
  useEffect(() => {
    if (selectedDepartments?.length) {
      const newSelection = selectedDepartments.filter(
        (department: SelectedComponentValue) => {
          return departments.some(({ value }) => value === department.value);
        }
      );
      if (newSelection.length !== selectedDepartments.length) {
        setValue('store.department', newSelection);
      }
    }
  }, [departments]);

  const toggleDefaultLocation = (value: boolean) => {
    if (value) {
      setValue('location.country', user?.country.toLowerCase());
      setValue('location.site', user?.cdsLocation);
    } else {
      resetField('location.country');
      resetField('location.site');
    }
  };

  return (
    <>
      <p className="leading-normal font-bold my-4">{t('label.location')}</p>
      <ControlledSwitch
        name="defaultLocation"
        control={control}
        errors={errors}
        label={t('label.defaultLocation', { location: user?.cdsLocation })}
        onChange={toggleDefaultLocation}
      />
      {!defaultLocation && (
        <div className="grid lg:grid-cols-2 lg:gap-4">
          <ControlledDropdown
            name="location.country"
            label={t('label.country')}
            control={control}
            errors={errors}
            items={countries}
          />
          <ControlledDropdown
            name="location.site"
            label={t('label.site')}
            control={control}
            errors={errors}
            items={sites}
            readOnly={!country || !sites.length}
          />
        </div>
      )}
      <ControlledSwitch
        name="onlyOnlineUsage"
        control={control}
        errors={errors}
        label={t('label.onlyOnlineUsage')}
      />
      <div className="grid lg:grid-cols-2 lg:gap-4">
        {locatedInStore && !onlyOnlineUsage && (
          <>
            <ControlledCombobox
              name="store.component"
              errors={errors}
              items={storeComponents}
              label={t('label.component')}
              placeholder={t('label.componentPlaceholder')}
              shouldValidate={true}
            />
            {!!selectedComponents?.length && !!departments?.length && (
              <ControlledCombobox
                name="store.department"
                errors={errors}
                items={departments}
                label={t('label.department')}
                shouldValidate={true}
                readOnly={storeComponents.length === 0}
                placeholder={t('label.departmentPlaceholder')}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}

export default Location;
